import React from "react";
import { Link } from "react-router-dom";
import { Star, ArrowLeft } from "lucide-react";
import MarketingLayout from "./MarketingLayout";
import { useSeo } from "@/lib/seo";
import { TESTIMONIALS, AVG_RATING, REVIEW_COUNT } from "@/lib/testimonials";

/**
 * Reviews — full testimonials page.
 *
 * Two SEO concerns it serves:
 *  1. Crawler visibility — every quote is in the static HTML, so
 *     Google can see the variety of locations + attributions.
 *  2. Schema.org JSON-LD: a `Product` (FamFeel) with an `AggregateRating`
 *     and a list of `Review` objects. Google may pull rich-result stars
 *     into the SERP listing for branded queries.
 *
 * No reading-mode tricks (no rotator, no carousel) — this page is
 * a humble, dense reading list. Visitors who follow the "Read every
 * review →" link from /landing want to read, not be tour-guided.
 */
function StarsRow({ count }) {
  return (
    <div
      className="flex items-center gap-0.5"
      aria-label={`Rated ${count} out of 5`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className="w-3.5 h-3.5"
          strokeWidth={0}
          fill={i < count ? "var(--ink)" : "var(--line)"}
        />
      ))}
    </div>
  );
}

const COUNTRY_LABEL = {
  US: "United States",
  UK: "United Kingdom",
  DK: "Denmark",
  SE: "Sweden",
  NO: "Norway",
  ES: "Spain",
  IT: "Italy",
  CY: "Cyprus",
  MT: "Malta",
};

export default function Reviews() {
  useSeo({
    title: "Reviews — what families say about FamFeel",
    description: `Real reviews of FamFeel, the calm shared family calendar and check-in app. ${REVIEW_COUNT} reviews from families across the UK, US, Denmark, Sweden, Norway, Spain, Italy, Cyprus and Malta. Average rating ${AVG_RATING}/5.`,
    path: "/reviews",
    jsonLdId: "ld-reviews",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "FamFeel",
      description:
        "A calm shared family calendar and check-in app for households of 4–18.",
      brand: { "@type": "Brand", name: "FamFeel" },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: String(AVG_RATING),
        reviewCount: String(REVIEW_COUNT),
        bestRating: "5",
        worstRating: "1",
      },
      review: TESTIMONIALS.map((t) => ({
        "@type": "Review",
        reviewBody: t.quote,
        datePublished: t.date,
        author: {
          "@type": "Person",
          name: t.name,
          address: {
            "@type": "PostalAddress",
            addressLocality: t.location,
            addressCountry: t.country,
          },
        },
        reviewRating: {
          "@type": "Rating",
          ratingValue: String(t.rating),
          bestRating: "5",
          worstRating: "1",
        },
      })),
    },
  });

  // Group by country so the page reads as a quiet world tour rather
  // than a dump-list. Order: anchor markets first, then alphabetical.
  const order = ["UK", "US", "DK", "SE", "NO", "ES", "IT", "CY", "MT"];
  const byCountry = order
    .map((c) => ({
      country: c,
      label: COUNTRY_LABEL[c] || c,
      items: TESTIMONIALS.filter((t) => t.country === c),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <MarketingLayout>
      <section className="px-6 md:px-10 pt-12 md:pt-20 pb-10 max-w-4xl">
        <Link
          to="/"
          className="label-tiny text-[color:var(--ink-2)] hover:text-[color:var(--ink)] inline-flex items-center gap-1 mb-8"
          data-testid="reviews-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
          Back to FamFeel
        </Link>
        <div className="label-tiny mb-4">Reviews</div>
        <h1
          className="font-display-italic text-5xl md:text-7xl leading-[0.95] tracking-tight mb-6"
          data-testid="reviews-h1"
        >
          What families say.
        </h1>
        <p className="text-lg md:text-xl text-[color:var(--ink-2)] max-w-2xl leading-snug mb-2">
          {REVIEW_COUNT} reviews from families across the UK, US, Denmark,
          Sweden, Norway, France, Spain, Italy, Australia and Malta. Average{" "}
          <strong>{AVG_RATING}/5</strong>.
        </p>
        <p className="text-sm text-[color:var(--ink-3)] max-w-2xl leading-snug">
          We don't pay for reviews and we don't moderate them. If a family
          drops out we leave their words intact — it'd be dishonest not to.
        </p>
      </section>

      <section className="px-6 md:px-10 pb-20 max-w-4xl">
        {byCountry.map((group) => (
          <div key={group.country} className="mb-12" data-testid={`reviews-group-${group.country}`}>
            <h2 className="label-tiny text-[color:var(--ink-3)] mb-5">
              {group.label} · {group.items.length}
            </h2>
            <ul className="space-y-6">
              {group.items.map((t) => (
                <li
                  key={t.id}
                  className="ff-card p-5 md:p-6"
                  data-testid={`review-${t.id}`}
                >
                  <StarsRow count={t.rating} />
                  <blockquote className="font-display-italic text-xl md:text-[1.6rem] leading-snug tracking-tight mt-3 text-[color:var(--ink)]">
                    {t.quote}
                  </blockquote>
                  <div className="mt-4 text-sm text-[color:var(--ink-2)]">
                    <span className="font-medium text-[color:var(--ink)]">
                      {t.name}
                    </span>
                    <span aria-hidden="true"> · </span>
                    <span>{t.location}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="mt-10 ff-card p-6 md:p-8 text-center">
          <div className="font-display-italic text-2xl md:text-3xl leading-snug mb-3">
            Want your family on this page?
          </div>
          <p className="text-[color:var(--ink-2)] mb-5 text-sm md:text-base">
            Use FamFeel for a fortnight, then drop us a note from the app:
            Settings → Family → "Send the makers a note." If we publish it
            we'll never use your real surname or your kids' names — first
            name + city only, like the page above.
          </p>
          <Link
            to="/app?mode=create"
            className="ff-btn ff-btn-primary"
            data-testid="reviews-cta"
          >
            Start your family →
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
